/**
 * Sino de avisos do app. Mostra os avisos do usuário na assessoria atual (mensagens novas, planilha publicada),
 * atualiza a cada 60 s (e quando uma conversa é aberta) e leva ao lugar certo conforme a área (atleta ou equipe).
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { api } from "../api/client";
import type { AppNotification, NotificationList } from "../api/types";
import { formatDateTime } from "../lib/format";
import { navigate } from "../router/router";
import { IconBell } from "./icons";
import { t, tn } from "../i18n";

export function NotificationBell({
  orgId,
  mode,
  placement = "down",
}: {
  orgId: string;
  mode: "athlete" | "staff";
  placement?: "down" | "up";
}) {
  const [data, setData] = useState<NotificationList | null>(null);
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const root = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get<NotificationList>(`/orgs/${orgId}/notifications`, { limit: 30 }));
    } catch {
      /* o sino é secundário: falha silenciosa, tenta de novo no próximo ciclo */
    }
  }, [orgId]);

  useEffect(() => {
    setData(null);
    void load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, 60_000);
    const refresh = () => void load();
    window.addEventListener("eapace:notifications", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("eapace:notifications", refresh);
    };
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function target(n: AppNotification): string {
    if (n.kind === "plan_published") return `/org/${orgId}/atleta/calendario`;
    if (n.kind === "support_reply") return n.ticket_id ? `/org/${orgId}/ajuda/chamados/${n.ticket_id}` : `/org/${orgId}/ajuda#suporte`;
    if (mode === "athlete") return n.activity_id ? `/org/${orgId}/atleta/atividades/${n.activity_id}` : `/org/${orgId}/atleta/mensagens`;
    if (!n.athlete_id) return `/org/${orgId}/atletas`;
    return n.activity_id ? `/org/${orgId}/atletas/${n.athlete_id}/atividades/${n.activity_id}` : `/org/${orgId}/atletas/${n.athlete_id}#conversa`;
  }

  async function openItem(n: AppNotification) {
    setOpen(false);
    if (!n.read) {
      try {
        await api.post(`/orgs/${orgId}/notifications/${n.id}/read`);
      } catch {
        /* segue para o destino mesmo assim */
      }
      void load();
    }
    navigate(target(n));
  }

  async function readAll() {
    try {
      await api.post(`/orgs/${orgId}/notifications/read-all`);
    } catch {
      /* se falhar, o próximo ciclo mostra o estado real */
    }
    void load();
  }

  const unread = data?.unread_count ?? 0;
  return (
    <div className={`bell bell-${placement}`} ref={root}>
      <button
        className="btn btn-ghost btn-sm bell-btn"
        aria-label={unread ? tn("Avisos: {n} não lido", "Avisos: {n} não lidos", unread) : t("Avisos")}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          setOpen((v) => !v);
          void load();
        }}
      >
        <IconBell width={20} height={20} />
        {unread > 0 && (
          <span className="bell-badge" aria-hidden>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="bell-panel" id={panelId} role="region" aria-label={t("Avisos")}>
          <div className="bell-head">
            <strong>{t("Avisos")}</strong>
            {unread > 0 && (
              <button className="bell-link" onClick={() => void readAll()}>
                {t("Marcar todos como lidos")}
              </button>
            )}
          </div>
          {!data || data.items.length === 0 ? (
            <p className="muted bell-empty">{data ? t("Nenhum aviso por enquanto.") : t("Carregando…")}</p>
          ) : (
            <ul className="bell-list">
              {data.items.map((n) => (
                <li key={n.id}>
                  <button className={`bell-item ${n.read ? "" : "unread"}`} onClick={() => void openItem(n)}>
                    <span className="bell-title">{n.title}</span>
                    {n.body && <span className="bell-body">{n.body}</span>}
                    <time className="bell-time" dateTime={n.created_at}>
                      {formatDateTime(n.created_at)}
                    </time>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
